/* eslint-disable replo/no-tailwind-literal-color */

import React, { useEffect } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import {
  SandpackCodeEditor,
  SandpackFileExplorer,
  SandpackPreview,
} from "@codesandbox/sandpack-react";
import Button from "@replo/design-system/components/button/Button";
import { skipToken } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useReactRenderStore } from "../store/reactRenderStore";
import EditorSandpackProvider from "./EditorSandpackProvider";

function ReactRender() {
  const projectId = useCurrentProjectId();
  const { selectedElement, setSelectedElement, setEditText, displayMode } =
    useReactRenderStore();
  const { pageId } = useParams();

  const { data: files = [], isLoading: isLoadingFiles } =
    trpc.file.list.useQuery(projectId ? { projectId } : skipToken);

  // ToDo: this should only get the product ids that are being used in the pages
  const { data: products } = trpc.product.list.useQuery(
    projectId ? { projectId, pageSize: 100 } : skipToken,
  );
  const productIds = products?.products.map((product) => product.id);

  const { data: page, isLoading: isLoadingPage } = trpc.file.find.useQuery(
    projectId ? { projectId, fileId: pageId ?? "" } : skipToken,
    {
      enabled: Boolean(projectId && pageId),
    },
  );

  useEffect(() => {
    if (selectedElement) {
      setEditText(selectedElement.text);
    }
    if (selectedElement === null) {
      // Find the iframe (adjust selector as needed)
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "CLEAR_SELECTION" }, "*");
      }
    }
  }, [selectedElement, setEditText]);
  const [isDebugModeOn, setIsDebugModeOn] = React.useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "ELEMENT_SELECTED") {
        setSelectedElement(event.data.payload);
      }
    };

    void window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setSelectedElement]);

  if (isLoadingFiles || isLoadingPage) {
    return <div>Loading...</div>;
  }

  if (!page) {
    return null;
  }

  const scale = 1;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-2">
        <Button
          onClick={() => setIsDebugModeOn(!isDebugModeOn)}
          variant={isDebugModeOn ? "secondary" : "primary"}
        >
          {isDebugModeOn ? "Debug On" : "Debug Off"}
        </Button>
      </div>
      <div className="relative h-full w-full overflow-hidden">
        <div
          style={{
            transformOrigin: "top left",
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
            overflow: "auto",
          }}
          className="flex h-full w-full flex-col"
        >
          <EditorSandpackProvider
            files={files}
            productIds={productIds ?? []}
            openPages={[page.path]}
            displayMode={displayMode}
          >
            <div className="flex h-full w-full flex-col">
              {/* DEBUG HELPER  */}
              {isDebugModeOn && (
                <div className="max-h-[30rem] overflow-y-auto">
                  <SandpackCodeEditor showTabs={false} />
                  <SandpackFileExplorer />
                </div>
              )}
              {/* END DEBUG HELPER */}

              <div className="flex h-full w-full flex-col overflow-y-auto">
                <SandpackPreview
                  showOpenInCodeSandbox={false}
                  showRefreshButton={false}
                  startRoute={page.path}
                  className="h-full w-full rounded-none"
                />
              </div>
            </div>
          </EditorSandpackProvider>
        </div>
      </div>
    </div>
  );
}

export default ReactRender;
